/**
 * Study Mode Pricing Page
 */

import {useState} from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {SEO} from '../../components/SEO';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface PricingPlan {
  name: string;
  price: {monthly: number; annual: number};
  description: string;
  features: string[];
  cta: {label: string; href: string};
  popular?: boolean;
  badge?: string;
}

const plans: PricingPlan[] = [
  {
    name: 'Free',
    price: {monthly: 0, annual: 0},
    description: 'Perfect for trying out Study Mode',
    features: [
      '5 documents/month',
      'Basic AI analysis',
      'Flashcard generation (up to 20 cards)',
      'Chat assistance',
      'Basic formatting',
      'Community support',
    ],
    cta: {label: 'Start Free', href: ROUTES.SIGN_IN},
  },
  {
    name: 'Premium',
    price: {monthly: 9.99, annual: 99.99},
    description: 'For serious students',
    features: [
      'Unlimited documents',
      'Advanced AI analysis',
      'Unlimited flashcard generation',
      'Voice assistant mode',
      'Visual diagram generation',
      'Spaced repetition',
      'Knowledge graphs',
      'Priority support',
    ],
    cta: {label: 'Get Started', href: `${ROUTES.SIGN_IN}?plan=study-premium`},
    popular: true,
  },
  {
    name: 'Student',
    price: {monthly: 4.99, annual: 49.99},
    description: 'Special pricing for verified students',
    features: [
      'Unlimited documents',
      'All Premium features',
      'Student verification required',
      'Academic year pricing',
      'Priority support',
    ],
    cta: {label: 'Verify Student', href: `${ROUTES.SIGN_IN}?plan=study-student`},
    badge: 'Best Value',
  },
];

export function StudyModePricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const {ref, isVisible} = useScrollAnimation();

  return (
    <>
      <SEO
        title="Study Mode Pricing - VoxaPilot"
        description="Choose the perfect plan for your study needs. Start free or upgrade to unlock advanced features."
        keywords={[
          'study mode pricing',
          'AI study tool pricing',
          'student pricing',
          'study assistant cost',
        ]}
      />
      <LandingLayout>
        <Section padding="lg" background="white" ref={ref} className="pt-32 pb-16 bg-background dark:bg-[#1E1E1E]">
          <Container>
            <div className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              {/* Header */}
              <div className="text-center mb-12">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                  Study Mode Pricing
                </h1>
                <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] max-w-2xl mx-auto mb-8">
                  Simple, transparent pricing designed for students. Start free, upgrade when you're ready.
                </p>

                {/* Billing Toggle */}
                <div className="flex items-center justify-center gap-4 mb-8">
                  <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-textPrimary dark:text-[#CCCCCC]' : 'text-textSecondary dark:text-[#858585]'}`}>
                    Monthly
                  </span>
                  <button
                    onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                    className="relative w-14 h-8 bg-gray-300 dark:bg-gray-700 rounded-full transition-colors"
                  >
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                      billingPeriod === 'annual' ? 'translate-x-6' : ''
                    }`}></div>
                  </button>
                  <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-textPrimary dark:text-[#CCCCCC]' : 'text-textSecondary dark:text-[#858585]'}`}>
                    Annual
                    <span className="ml-2 text-xs text-orange-600 dark:text-orange-500">Save 17%</span>
                  </span>
                </div>
              </div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
                {plans.map((plan, index) => {
                  const price = billingPeriod === 'annual' ? plan.price.annual : plan.price.monthly;
                  const displayPrice = price === 0 ? 'Free' : `$${price.toFixed(2)}`;
                  const period = price === 0 ? '' : billingPeriod === 'annual' ? '/year' : '/month';
                  
                  return (
                    <div
                      key={plan.name}
                      className={`transition-all duration-700 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                      }`}
                      style={{transitionDelay: `${index * 0.1}s`}}
                    >
                      <Card
                        hover={plan.popular}
                        className={`p-8 h-full flex flex-col relative transition-all duration-300 ${
                          plan.popular 
                            ? 'border-2 border-gray-900 dark:border-gray-700 shadow-xl dark:shadow-gray-900/50 scale-105' 
                            : 'border border-gray-200 dark:border-gray-700 hover:shadow-lg dark:hover:shadow-gray-900/30'
                        }`}
                      >
                        {(plan.popular || plan.badge) && (
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                            <span className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-1.5 rounded-full text-xs font-semibold shadow-md">
                              {plan.popular ? 'Popular' : plan.badge}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-col h-full">
                          {/* Plan Name */}
                          <div className="mb-6">
                            <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
                              {plan.name}
                            </h3>
                            <p className="text-sm text-textSecondary dark:text-[#858585]">
                              {plan.description}
                            </p>
                          </div>

                          {/* Price */}
                          <div className="mb-6">
                            <div className="flex items-baseline gap-2">
                              <span className="text-4xl font-bold text-textPrimary dark:text-[#CCCCCC]">
                                {displayPrice}
                              </span>
                              {price > 0 && (
                                <span className="text-textSecondary dark:text-[#858585]">
                                  {period}
                                </span>
                              )}
                            </div>
                            {billingPeriod === 'annual' && price > 0 && (
                              <p className="text-sm text-textTertiary dark:text-[#6A6A6A] mt-1">
                                ${(price / 12).toFixed(2)}/month billed annually
                              </p>
                            )}
                          </div>

                          {/* Features */}
                          <ul className="flex-1 space-y-3 mb-8">
                            {plan.features.map((feature) => (
                              <li key={feature} className="flex items-start gap-3">
                                <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-textSecondary dark:text-[#858585]">
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>

                          {/* CTA */}
                          <Link to={plan.cta.href} className="mt-auto">
                            <Button
                              variant={plan.popular ? 'primary' : 'outline'}
                              size="md"
                              className="w-full"
                            >
                              {plan.cta.label}
                            </Button>
                          </Link>
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>

              {/* FAQ Section */}
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-6 text-center">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                      Can I change plans later?
                    </h3>
                    <p className="text-sm text-textSecondary dark:text-[#858585]">
                      Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges.
                    </p>
                  </div>
                  <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                      What payment methods do you accept?
                    </h3>
                    <p className="text-sm text-textSecondary dark:text-[#858585]">
                      We accept all major credit cards, debit cards, and PayPal. Student plans may require verification.
                    </p>
                  </div>
                  <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                      Is there a free trial?
                    </h3>
                    <p className="text-sm text-textSecondary dark:text-[#858585]">
                      Yes! The Free plan is available forever with no credit card required. You can upgrade anytime to unlock more features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </Section>
      </LandingLayout>
    </>
  );
}

